import React, { useEffect } from 'react';
import logoUrl from '../../assets/dr-signs-logo.png';
import './speakup.css';

// ---------------------------------------------------------------------------
// Gate decision D28 — Speak Up's own visual identity.
//
// Colby's ruling: "Speak Up must not outwardly appear to be part of Stede."
//
// WHAT THAT ACTUALLY MEANT IN THE CODE, and it was more than a logo swap. The
// three public pages each rendered their own copy of a header that was Stede's
// down to the pixel: a black operations bar, a white rounded square holding the
// letter S, condensed uppercase headings in Stede's --font-head, and Stede's
// own surface tokens underneath. Somebody arriving from a QR code on a wall was
// looking at the inventory app wearing a different title.
//
// So this replaces all three copies with one header, and the shell it sits in
// declares its own variables rather than inheriting Stede's. The two products
// now differ in the three things a person actually registers before reading
// anything:
//
//   ground      Stede is a dark chrome over off-white. Speak Up is warm paper
//               with no chrome at all.
//   type        Stede heads are condensed, uppercase, letterspaced — the look
//               of a dense operations tool. Speak Up heads are a serif at a
//               normal width, which reads as correspondence rather than a
//               dashboard.
//   mark        Stede's S becomes D&R's own logo. The company's name stays,
//               which is the half of D28 people forget: it is not anonymous
//               software, it is D&R's own channel.
//
// AND THE HEADER IS NOT A LINK TO ANYTHING. There is no navigation, no account
// menu and no way out except the page itself. On this origin the application
// does not exist (see App.js), so a header that offered a way "back" would be
// offering a door that isn't there.
//
// THIS FILE BRIEFLY BROKE ITS OWN RULE AND COLBY CAUGHT IT. The first version
// put a "What we've heard" link in the header — which put TWO doors to the
// board six inches apart, since the front door already carries one in the body
// where gate callout A2 places it. A rule stated in a comment and violated in
// the same file is worth recording rather than quietly deleting.
// ---------------------------------------------------------------------------
// A NEUTRAL MARK FOR THE TAB. Stede's favicon is a green ring on a dark
// ground and is the single most recognisable thing about the operations app,
// so on speakup.team it was announcing Stede in the tab strip. This is a plain
// speech bubble in Speak Up's own ink, which says nothing about any employer.
const SPEAKUP_FAVICON =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E" +
  "%3Crect width='100' height='100' rx='22' fill='%23FBFAF7'/%3E" +
  "%3Cpath d='M22 28h56a8 8 0 018 8v28a8 8 0 01-8 8H50L32 84V72h-10a8 8 0 01-8-8V36a8 8 0 018-8z' " +
  "fill='none' stroke='%231A1917' stroke-width='7' stroke-linejoin='round'/%3E%3C/svg%3E";

export default function SpeakUpShell({ children }) {
  // D28, the half that lives in the browser rather than on the page.
  //
  // FOUND BY LOOKING AT THE TAB, not by reading the code: speakup.team served
  // the title "Stede - Every part in place." and Stede's own favicon, because
  // both come from public/index.html and every route inherits them. So the
  // page went to great lengths not to look like Stede while the browser said
  // "Stede" an inch above it.
  //
  // That is not cosmetic on THIS page. Somebody filing an anonymous report
  // reads their own screen in a shared office, and a tab naming the company's
  // internal tool is exactly the association the channel exists to break.
  //
  // The title is "Speak Up" alone rather than "Speak Up - D&R Signs". D28
  // keeps D&R's name where the reporter reads it -- the header carries the
  // mark and the company name -- but a tab strip is read over shoulders, and
  // it does not need to carry an employer's name to do its job.
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Speak Up';

    const icon = document.querySelector("link[rel='icon']");
    const previousIcon = icon ? icon.getAttribute('href') : null;
    if (icon) icon.setAttribute('href', SPEAKUP_FAVICON);

    const desc = document.querySelector("meta[name='description']");
    const previousDesc = desc ? desc.getAttribute('content') : null;
    if (desc) {
      desc.setAttribute('content', 'A private way to raise an idea or a concern at work.');
    }

    // Restored on unmount so a signed-in employee who reaches /speak-up from
    // inside Stede and navigates away does not keep Speak Up's chrome.
    return () => {
      document.title = previousTitle;
      if (icon && previousIcon) icon.setAttribute('href', previousIcon);
      if (desc && previousDesc) desc.setAttribute('content', previousDesc);
    };
  }, []);

  return (
    <div className="speakup-page">
      <header className="speakup-bar">
        <img src={logoUrl} alt="D&R Signs" className="speakup-mark" />
        <div className="speakup-bar-text">
          <span className="speakup-bar-title">Speak Up</span>
          <span className="speakup-bar-sub">D&amp;R Signs</span>
        </div>
      </header>
      <div className="speakup-pad">{children}</div>
      <footer className="speakup-foot">
        Nothing on this page records who you are.
      </footer>
    </div>
  );
}
